import { ReactNode } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { MoonLoader } from 'react-spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';

// ─── alert / inline notice ──────────────────────────────────────────────────

export type AlertTone = 'info' | 'success' | 'warning' | 'error' | 'neutral';

const ALERT: Record<AlertTone, { bg: string; border: string; fg: string; icon: any }> = {
  info: { bg: '#EFF6FF', border: '#BFDBFE', fg: '#1E40AF', icon: faCircleInfo },
  success: { bg: '#ECFDF5', border: '#A7F3D0', fg: '#065F46', icon: faCircleCheck },
  warning: { bg: '#FFFBEB', border: '#FDE68A', fg: '#92400E', icon: faTriangleExclamation },
  error: { bg: '#FEF2F2', border: '#FECACA', fg: '#991B1B', icon: faCircleExclamation },
  neutral: {
    bg: 'var(--color-bg-secondary)',
    border: 'var(--color-border)',
    fg: 'var(--color-text-primary)',
    icon: faCircleInfo,
  },
};

const AlertRoot = styled.div<{ $tone: AlertTone }>`
  ${tw`flex gap-3 w-full p-4 text-sm`}
  border-radius: var(--radius-lg);
  background-color: ${({ $tone }) => ALERT[$tone].bg};
  border: 1px solid ${({ $tone }) => ALERT[$tone].border};
  color: ${({ $tone }) => ALERT[$tone].fg};
`;

const AlertIcon = styled.span`
  ${tw`shrink-0 mt-0.5`}
`;

const AlertBody = styled.div`
  ${tw`min-w-0 flex-1 leading-6`}
`;

const AlertTitle = styled.div`
  ${tw`font-semibold`}
`;

const AlertActions = styled.div`
  ${tw`shrink-0 flex items-center gap-2`}
`;

export function Alert({
  tone = 'info',
  title,
  children,
  actions,
  className,
  icon = true,
  'data-testid': testId,
}: {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
  icon?: boolean;
  'data-testid'?: string;
}) {
  return (
    <AlertRoot
      $tone={tone}
      role={tone === 'error' ? 'alert' : 'status'}
      className={className}
      data-testid={testId}
    >
      {icon && (
        <AlertIcon aria-hidden>
          <FontAwesomeIcon icon={ALERT[tone].icon} />
        </AlertIcon>
      )}
      <AlertBody>
        {title && <AlertTitle>{title}</AlertTitle>}
        {children}
      </AlertBody>
      {actions && <AlertActions>{actions}</AlertActions>}
    </AlertRoot>
  );
}

// ─── stat tile ──────────────────────────────────────────────────────────────

const StatRoot = styled.div`
  ${tw`flex flex-col gap-1 p-5 min-w-0`}
  background-color: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
`;

const StatLabel = styled.div`
  ${tw`text-xs font-semibold uppercase tracking-wider`}
  color: var(--color-text-secondary);
`;

const StatValue = styled.div`
  ${tw`text-2xl font-bold leading-tight tabular-nums`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
  letter-spacing: -0.02em;
`;

const StatHint = styled.div`
  ${tw`text-xs`}
  color: var(--color-text-secondary);
`;

export function StatCard({
  label,
  value,
  hint,
  className,
  'data-testid': testId,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <StatRoot className={className} data-testid={testId}>
      <StatLabel>{label}</StatLabel>
      <StatValue>{value}</StatValue>
      {hint && <StatHint>{hint}</StatHint>}
    </StatRoot>
  );
}

export const StatGrid = styled.div<{ $cols?: 2 | 3 | 4 }>`
  ${tw`grid grid-cols-1 gap-4 sm:grid-cols-2`}
  @media (min-width: 1024px) {
    grid-template-columns: repeat(${({ $cols = 4 }) => $cols}, minmax(0, 1fr));
  }
`;

// ─── loading ────────────────────────────────────────────────────────────────

const SpinnerRoot = styled.div<{ $minHeight?: string }>`
  ${tw`flex flex-col items-center justify-center gap-3 w-full text-sm`}
  min-height: ${({ $minHeight = '160px' }) => $minHeight};
  color: var(--color-text-secondary);
`;

export function LoadingBlock({
  label,
  minHeight,
  className,
}: {
  label?: ReactNode;
  minHeight?: string;
  className?: string;
}) {
  return (
    <SpinnerRoot $minHeight={minHeight} className={className} role="status" aria-live="polite">
      <MoonLoader size={22} color="var(--color-black)" />
      {label && <span>{label}</span>}
    </SpinnerRoot>
  );
}

// ─── key/value list ─────────────────────────────────────────────────────────

export const DescriptionList = styled.dl`
  ${tw`grid grid-cols-1 sm:grid-cols-[minmax(140px,220px)_1fr] gap-x-6 gap-y-3 text-sm m-0`}
`;

export const DT = styled.dt`
  ${tw`font-medium`}
  color: var(--color-text-secondary);
`;

export const DD = styled.dd`
  ${tw`m-0 min-w-0 break-words`}
  color: var(--color-text-primary);
`;

/** Inline divider between card sections. */
export const Divider = styled.hr`
  ${tw`w-full border-0 m-0`}
  height: 1px;
  background-color: var(--color-border);
`;
